import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { ArrowDown, Download } from 'lucide-react';
import myselfImg from '../../../assets/img/myself.png';
import resumeFile from '../../../assets/img/resume/ErvinTejada_Resume.pdf';
import '../../../styles/hero.css';

// Animation variants
const containerVariants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.12 } },
};

const itemVariants = {
  hidden:  { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: [0.25, 0.46, 0.45, 0.94] } },
};

const imageVariants = {
  hidden:  { opacity: 0, scale: 0.88 },
  visible: { opacity: 1, scale: 1, transition: { duration: 0.7, ease: [0.25, 0.46, 0.45, 0.94] } },
};

const roles = ["Software Engineer", "QA Automation Engineer"];

export function Hero({ onHireMeClick }) {
  const [text, setText] = useState("Software Engineer");
  const [isDeleting, setIsDeleting] = useState(true);
  const [loopNum, setLoopNum] = useState(0);
  const [typingSpeed, setTypingSpeed] = useState(2000);

  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);

  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    setPrefersReducedMotion(mediaQuery.matches);
    const handleMediaChange = (e) => setPrefersReducedMotion(e.matches);
    
    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', handleMediaChange);
      return () => mediaQuery.removeEventListener('change', handleMediaChange);
    }
  }, []);

  useEffect(() => {
    if (prefersReducedMotion) return;

    const handleType = () => {
      const currentFullText = roles[loopNum % roles.length];
      let nextText;
      
      if (isDeleting) {
        nextText = currentFullText.substring(0, text.length - 1);
      } else {
        nextText = currentFullText.substring(0, text.length + 1);
      }
      
      setText(nextText);

      let nextSpeed = isDeleting ? 40 : 65;

      if (!isDeleting && nextText === currentFullText) {
        nextSpeed = 2000;
        setIsDeleting(true);
      } else if (isDeleting && nextText === "") {
        setIsDeleting(false);
        setLoopNum(prev => prev + 1);
        nextSpeed = 500;
      }

      setTypingSpeed(nextSpeed);
    };

    const timer = setTimeout(handleType, typingSpeed);
    return () => clearTimeout(timer);
  }, [text, isDeleting, loopNum, typingSpeed, prefersReducedMotion]);

  const scrollToProjects = () => {
    document.getElementById('projects')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section className="hero" id="home" aria-label="Introduction">
      <div className="hero-container">

        {/* ---- Text Content ---- */}
        <motion.div
          className="hero-text"
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          <motion.span className="hero-eyebrow" variants={itemVariants}>
            <span className="hero-eyebrow-dot" aria-hidden="true" />
            Available for opportunities
          </motion.span>

          <motion.h1 className="hero-heading" variants={itemVariants}>
            <span>Hi, I'm</span>
            <span className="gradient-text">Vin Tejada</span>
          </motion.h1>

          <motion.p className="hero-role" variants={itemVariants}>
            I'm a{' '}
            {prefersReducedMotion ? (
              <span className="hero-role-highlight">Software Engineer</span>
            ) : (
              <>
                <span className="hero-role-highlight">{text}</span>
                <span className="typing-cursor" aria-hidden="true">|</span>
              </>
            )}
          </motion.p>

          <motion.p className="hero-description" variants={itemVariants}>
            Computer Science graduate from Mapúa University, specializing in
            Application Development. I build clean, performant, and user-friendly
            web and mobile experiences.
          </motion.p>

          <motion.div className="hero-ctas" variants={itemVariants}>
            <button
              className="btn-primary"
              onClick={scrollToProjects}
              aria-label="Scroll to projects section"
            >
              View Projects
              <ArrowDown size={16} strokeWidth={2} aria-hidden="true" />
            </button>
            <a
              href={resumeFile}
              download="ErvinTejada_Resume.pdf"
              className="btn-ghost"
              aria-label="Download Resume"
            >
              <Download size={16} strokeWidth={2} aria-hidden="true" />
              Resume
            </a>
          </motion.div>
        </motion.div>

        {/* ---- Profile Image ---- */}
        <motion.div
          className="hero-image-wrapper"
          variants={imageVariants}
          initial="hidden"
          animate="visible"
        >
          <div className="hero-image-glow" aria-hidden="true" />
          <div className="hero-image-ring" aria-hidden="true">
            <div className="hero-image-inner">
              <img
                src={myselfImg}
                alt="Vin Tejada — Software Engineer"
                loading="eager"
                fetchpriority="high"
              />
            </div>
          </div>
        </motion.div>

      </div>
    </section>
  );
}
